import { MenuItem, Spinner } from "@blueprintjs/core"
import { ItemPredicate, ItemRendererProps, Select } from "@blueprintjs/select"
import { useController, UseControllerProps } from "react-hook-form"

import { ToastError } from "../../../components"
import useTradingRegions from "../hooks/useTradingRegions"
import { Region } from "../types"
import { SelectButton } from "./SelectComponent.styled"

export const RegionSelect = <T extends Record<string, any>>({
  control,
  name,
  disabled,
}: UseControllerProps<T>) => {
  const {
    field: { value, onChange },
    fieldState: { isDirty },
  } = useController({ control, name })

  /**
   * Get the country list
   */
  const { regions, isLoading, error } = useTradingRegions()

  /**
   * generate Menu to render in the dropdown
   */
  const renderRegion = (item: Region, { handleClick, modifiers }: ItemRendererProps) => {
    if (!modifiers.matchesPredicate) return null
    return (
      <MenuItem
        active={modifiers.active}
        key={item.id}
        text={item.name}
        onClick={handleClick}
        roleStructure="listoption"
      />
    )
  }

  /**
   * Filter the country based on the query
   */
  const filterRegion: ItemPredicate<Region> = (query, region, index, exactMatch) => {
    const normalizedTitle = region.name.toLowerCase()
    const normalizedQuery = query.toLowerCase()

    if (exactMatch) {
      return normalizedTitle === normalizedQuery
    } else {
      return normalizedTitle.indexOf(normalizedQuery) >= 0
    }
  }

  return (
    <>
      {isLoading ? (
        <Spinner size={15} />
      ) : (
        <Select
          items={regions?.length ? regions : []} // we want to keep the selected country name displayed in the Button, so if no country list we display emmpty list
          itemRenderer={renderRegion}
          itemPredicate={filterRegion}
          onItemSelect={region => onChange(region)}
          noResults={<MenuItem disabled={true} text="No results" />}
          resetOnSelect={true}
          resetOnClose={true}
        >
          <SelectButton
            text={value ? (value as Region).name : "Select a region"}
            rightIcon="caret-down"
            small
            intent={isDirty ? "warning" : "none"}
            disabled={disabled || !!error}
            icon={disabled && "disable"}
          />
        </Select>
      )}
      <ToastError
        error={error}
        errorMessage="Unable to get countries, please try to refresh the page."
      />
    </>
  )
}
